// WindLegend — small status badge for the wind-particles layer.
//
// Shows:
//   - GFS cycle the rendered data came from (e.g. "GFS 12Z 20-May")
//   - Forecast offset ("Valid: NOW" or "Valid: +6h")
//   - Speed scale (legend bar showing how particle brightness maps to kt)
//
// Renders only when layers.wind is true. Lives top-right under the
// AlertCenter stack. Self-fetches /api/wind-manifest on mount.

import { useEffect, useState } from 'react';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { useWindPrefsStore } from '../stores/windPrefsStore';

interface ManifestQa {
  passed: boolean;
  validityScore: number;
  threshold: number;
}

interface Manifest {
  cycle: string;            // "20260520_12"
  generatedAt: string;      // ISO
  forecastHours: number[];
  /** QA block — added 2026-05-28. Absent on older cycles. */
  qa?: ManifestQa;
}

function fmtCycle(cycleKey: string): string {
  // "20260520_12" → "12Z 20-May"
  const m = cycleKey.match(/^(\d{4})(\d{2})(\d{2})_(\d{2})$/);
  if (!m) return cycleKey;
  const [, , mm, dd, hh] = m;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${hh}Z ${Number(dd)}-${months[Number(mm) - 1] ?? mm}`;
}

export function WindLegend() {
  const visible = useLayerVisibilityStore((s) => s.layers.wind);
  const hour = useWindPrefsStore((s) => s.windForecastHour);
  const [manifest, setManifest] = useState<Manifest | null>(null);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/wind-manifest');
        if (!r.ok) return;
        const data = (await r.json()) as Manifest;
        if (!cancelled) setManifest(data);
      } catch {
        /* swallow */
      }
    })();
    return () => { cancelled = true; };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      // Sits BELOW the MapTopBar so it doesn't block the Layers button.
      // MapTopBar uses `paddingTop: var(--safe-top)` + ~h-10 + ~pt-2 ≈ 56-72
      // px depending on notch. We push the legend to ~88 px from viewport top
      // (safe-top + 16 px gap). Was `top-3` — overlapped the Layers button.
      className="pointer-events-none fixed left-3 z-30 rounded-lg bg-black/65 px-3 py-2 text-white backdrop-blur-sm shadow-lg select-none"
      style={{ minWidth: 130, top: 'calc(var(--safe-top, 0px) + 64px)' }}
    >
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <span className="text-[10px] uppercase tracking-wide opacity-70">Wind</span>
        {manifest && (
          <span className="text-[10px] font-mono opacity-60">{fmtCycle(manifest.cycle)}</span>
        )}
        {/* Validity-uncertain chip — only when qa block present AND passed===false */}
        {manifest?.qa != null && manifest.qa.passed === false && (
          <span
            className="pointer-events-auto cursor-help text-[9px] font-semibold rounded px-1 py-0.5 bg-yellow-400/20 text-yellow-300 border border-yellow-500/40"
            title={`Wind validity uncertain (score ${(manifest.qa.validityScore * 100).toFixed(0)}% < ${(manifest.qa.threshold * 100).toFixed(0)}% threshold). Auto-detected pressure centers don’t match expected rotation. Cross-check against Surface Analysis layer.`}
          >
            ⚠ validity uncertain
          </span>
        )}
      </div>
      <div className="text-xs font-mono tabular-nums mb-1.5">
        Valid: <span className="font-bold">{hour === 0 ? 'NOW' : `+${hour}h`}</span>
      </div>
      {/* Speed legend — matches the WindFillLayer gradient (kt).
          0–5 slate, 5–10 light blue, 10–20 green, 20–30 yellow,
          30–40 orange, 40–50 red, 50+ purple. */}
      <div className="flex items-end gap-1.5">
        <div
          className="h-2 flex-1 rounded-sm"
          style={{
            background:
              'linear-gradient(90deg, #738498 0%, #8DC7ED 8%, #66D973 25%, #F2EB40 40%, #FA9933 55%, #ED4052 70%, #A633A6 85%, #481A61 100%)',
          }}
        />
      </div>
      <div className="flex justify-between text-[9px] tabular-nums opacity-60 mt-0.5">
        <span>0</span>
        <span>10</span>
        <span>20</span>
        <span>30</span>
        <span>40</span>
        <span>50+ kt</span>
      </div>
    </div>
  );
}
